import { useState } from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import { BRAND, BUSINESSES, CAPABILITIES } from "@/data/content";
import { STRATEGY_SUBPAGES } from "@/data/strategySubPages";
import { INTELLIGENCE_SUBPAGES } from "@/data/intelligenceSubPages";
import { BRAND_SUBPAGES } from "@/data/brandSubPages";
import { DIGITAL_SUBPAGES } from "@/data/digitalSubPages";
import { INDUSTRIES } from "@/data/industries";
import { INSIGHTS } from "@/data/insights";
import { usePageMeta } from "@/lib/usePageMeta";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

const SUBPAGES_BY_CAPABILITY = {
  "strategy-commercial-transformation": STRATEGY_SUBPAGES,
  "market-customer-intelligence": INTELLIGENCE_SUBPAGES,
  "brand-marketing-communications": BRAND_SUBPAGES,
  "digital-commerce": DIGITAL_SUBPAGES,
};

const COMPANY_LINKS = [
  { to: "/", label: "Home" },
  { to: "/leadership", label: "Leadership" },
  { to: "/ecosystem", label: "Ecosystem" },
  { to: "/insights", label: "Insights" },
  { to: "/solutions", label: "Solutions" },
  { to: "/how-we-engage", label: "How We Engage" },
  { to: "/contact", label: "Contact" },
  { to: "/careers", label: "Careers" },
];

const LEGAL_LINKS = [
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/terms-of-use", label: "Terms of Use" },
  { to: "/sitemap", label: "Sitemap" },
];

function LinkItem({ to, label, testid }) {
  return (
    <li>
      <Link
        to={to}
        data-testid={testid}
        className="text-sm text-zinc-600 hover:text-black transition-colors"
      >
        {label}
      </Link>
    </li>
  );
}

export default function SitemapPage() {
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);

  usePageMeta({
    title: `Sitemap | ${BRAND.fullName}`,
    description:
      "A complete index of every page on blezzings.co — services, industries, insights, company and legal pages.",
    canonical: "https://blezzings.co/sitemap",
    breadcrumbs: [
      { name: "Home", url: "https://blezzings.co/" },
      { name: "Sitemap", url: "https://blezzings.co/sitemap" },
    ],
  });

  return (
    <div data-testid="sitemap-page" className="relative">
      <Nav onBook={() => setBookOpen(true)} />

      <section className="pt-32 md:pt-40 pb-12 md:pb-16 bg-[#fafafa]">
        <div className={GRID}>
          <SectionBadge label="Explore" />
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] max-w-3xl">
            Sitemap
          </h1>
          <p className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-2xl">
            Every page on blezzings.co, in one place.
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-24 bg-[#fafafa]">
        <div className={`${GRID} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12 border-t border-black/10 pt-10`}>
          <div>
            <div className="overline text-zinc-400">Company</div>
            <ul className="mt-4 space-y-2">
              {COMPANY_LINKS.map((l) => (
                <LinkItem key={l.to} {...l} testid={`sitemap-company-${l.label.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </ul>
          </div>

          <div>
            <div className="overline text-zinc-400">Ecosystem</div>
            <ul className="mt-4 space-y-2">
              {BUSINESSES.map((b) => (
                <LinkItem
                  key={b.slug}
                  to={`/businesses/${b.slug}`}
                  label={b.name}
                  testid={`sitemap-business-${b.slug}`}
                />
              ))}
            </ul>
          </div>

          <div>
            <div className="overline text-zinc-400">Industries</div>
            <ul className="mt-4 space-y-2">
              {INDUSTRIES.map((i) => (
                <LinkItem
                  key={i.slug}
                  to={`/industries/${i.slug}`}
                  label={i.name}
                  testid={`sitemap-industry-${i.slug}`}
                />
              ))}
            </ul>
          </div>

          {CAPABILITIES.map((c) => (
            <div key={c.slug}>
              <Link
                to={`/capabilities/${c.slug}`}
                data-testid={`sitemap-capability-${c.slug}`}
                className="overline text-zinc-900 hover:text-zinc-500 transition-colors"
              >
                {c.name}
              </Link>
              <ul className="mt-4 space-y-2">
                {(SUBPAGES_BY_CAPABILITY[c.slug] || []).map((s) => (
                  <LinkItem
                    key={s.slug}
                    to={`/capabilities/${c.slug}/${s.slug}`}
                    label={s.title}
                    testid={`sitemap-subpage-${s.slug}`}
                  />
                ))}
              </ul>
            </div>
          ))}

          <div>
            <div className="overline text-zinc-400">Insights</div>
            <ul className="mt-4 space-y-2">
              {INSIGHTS.map((a) => (
                <LinkItem
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  label={a.title}
                  testid={`sitemap-insight-${a.slug}`}
                />
              ))}
            </ul>
          </div>

          <div>
            <div className="overline text-zinc-400">Legal</div>
            <ul className="mt-4 space-y-2">
              {LEGAL_LINKS.map((l) => (
                <LinkItem key={l.to} {...l} testid={`sitemap-legal-${l.label.toLowerCase().replace(/\s+/g, "-")}`} />
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => setBookOpen(true)} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
